/**
 * Abbreviates a positive numeric value to K/M/B/T with locale-aware decimal formatting.
 *
 * Shared between `SgSmartCurrencyPipe` (compact + abbreviated modes), `SgEntityCountPipe`,
 * and `SgUnitsCountPipe`.
 * Callers handle their own sign, currency symbol, and below-threshold formatting; this
 * helper covers only the `≥ 1000` abbreviation tier.
 *
 * @param absValue         Absolute value to abbreviate (must be `≥ 1000`; smaller values
 *                         are the caller's concern).
 * @param decimalsForUnit  Strategy callback returning the max decimal places to render
 *                         for a given unit-value + suffix. Examples:
 *                         - sgSmartCurrency compact: `(uv) => uv >= 100 ? 0 : 1`
 *                         - sgSmartCurrency abbreviated: `(uv, unit) => unit === 'K' ? (uv >= 100 ? 0 : 1) : 2`
 *                         - sgEntityCount: same as compact.
 *                         - sgUnitsCount abbreviated: `() => 2` (uniform 2dp at M/B/T per spec §6).
 * @param locale           BCP-47 locale code for the decimal separator (`'en-US'` → `.`,
 *                         `'de-DE'` / `'fr-FR'` → `,`).
 * @returns                Formatted string like `"1.5K"`, `"847.5M"`, `"1,5K"` (de-DE).
 *                         Trailing zeros after the decimal separator are auto-trimmed
 *                         (`1.50K` → `1.5K`, `12.00K` → `12K`).
 */
export function abbreviateNumber(
  absValue: number,
  decimalsForUnit: (unitValue: number, unit: AbbreviationUnit) => number,
  locale: string
): string {
  if (absValue >= 1_000_000_000_000) return formatAbbreviationUnit(absValue / 1e12, 'T', decimalsForUnit, locale);
  if (absValue >= 1_000_000_000) return formatAbbreviationUnit(absValue / 1e9, 'B', decimalsForUnit, locale);
  if (absValue >= 1_000_000) return formatAbbreviationUnit(absValue / 1e6, 'M', decimalsForUnit, locale);

  return formatAbbreviationUnit(absValue / 1e3, 'K', decimalsForUnit, locale);
}

export type AbbreviationUnit = 'K' | 'M' | 'B' | 'T';

function formatAbbreviationUnit(
  unitValue: number,
  unit: AbbreviationUnit,
  decimalsForUnit: (unitValue: number, unit: AbbreviationUnit) => number,
  locale: string
): string {
  const decimals = decimalsForUnit(unitValue, unit);
  // `minimumFractionDigits: 0` auto-trims trailing zeros after the decimal separator —
  // `1.50` → `1.5`, `12.00` → `12`. `useGrouping: false` because unit-values are always
  // `< 1000` and never need a thousands separator.
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
    useGrouping: false,
  }).format(unitValue);

  return `${formatted}${unit}`;
}
